/**
 * Procedurální zvuky přes WebAudio — žádné soubory, jen syntéza.
 */

export class Sfx {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.muted = localStorage.getItem('sprtec2_muted') === '1';
  }

  /** AudioContext smí vzniknout až po gestu uživatele. */
  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return false;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.5;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    return true;
  }

  setMuted(m) {
    this.muted = m;
    localStorage.setItem('sprtec2_muted', m ? '1' : '0');
    if (this.master) this.master.gain.value = m ? 0 : 0.5;
  }

  osc(type, freq, t0, dur, peak = 0.4, freqEnd = null) {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (freqEnd) o.frequency.exponentialRampToValueAtTime(freqEnd, t0 + dur);
    g.gain.setValueAtTime(peak, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  noise(t0, dur, filterFreq, peak = 0.3) {
    const len = Math.ceil(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(peak, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    src.connect(f).connect(g).connect(this.master);
    src.start(t0);
  }

  /** Cvaknutí kamene o kámen/puk. */
  hit(strength = 0.5) {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    this.noise(t, 0.05, 2600 + strength * 1500, 0.25 + strength * 0.4);
    this.osc('sine', 1500 + strength * 600, t, 0.06, 0.12 + strength * 0.2, 700);
  }

  /** Tupý náraz do mantinelu. */
  wall(strength = 0.5) {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    this.noise(t, 0.08, 500 + strength * 300, 0.2 + strength * 0.3);
    this.osc('sine', 220, t, 0.09, 0.15 + strength * 0.15, 90);
  }

  /** Klapnutí UI. */
  click() {
    if (!this.ensure()) return;
    this.osc('sine', 660, this.ctx.currentTime, 0.05, 0.12, 480);
  }

  /** Gólová siréna + jásot. */
  goal() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    for (const f of [392, 494]) {
      this.osc('sawtooth', f, t, 0.55, 0.16, f * 1.35);
      this.osc('sawtooth', f * 1.01, t + 0.45, 0.5, 0.14, f * 1.4);
    }
    this.noise(t + 0.05, 1.3, 1200, 0.12);
  }

  /** Píšťalka rozhodčího. */
  whistle() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    this.osc('square', 2300, t, 0.14, 0.1);
    this.osc('square', 2300, t + 0.18, 0.26, 0.1);
  }

  win() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    [523, 659, 784, 1047].forEach((f, i) => this.osc('triangle', f, t + i * 0.13, 0.3, 0.2));
  }

  lose() {
    if (!this.ensure()) return;
    const t = this.ctx.currentTime;
    [392, 330, 262].forEach((f, i) => this.osc('triangle', f, t + i * 0.16, 0.35, 0.18));
  }
}
